/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { Box } from "@material-ui/core";
import {
    AppContainerV1,
    NotificationScopeV1,
    NotificationTypeV1,
    useAuthProviderV1,
    useNotificationProviderV1,
} from "@startree-ui/platform-ui";
import delay from "lodash/delay";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppHeader } from "./components/layout/app-header/app.header.component";
import { AppNavBar } from "./components/layout/app-nav-bar/app-nav-bar.component";
import { AppRouter } from "./routers/app/app.router";
import {
    fetchDataportalAPIFT,
    fetchPreviewTableAPIFT,
    shouldRedirectUserToDP,
} from "./utils/dataset-ingestion/dataset-ingestion-utils";
import { getDataManagerUiVersion } from "./utils/version/version.util";

// Data Manager UI app
export const App: FunctionComponent = () => {
    const [showAppNavBar, setShowAppNavBar] = useState(false);
    const {
        authDisabled,
        authDisabledNotification,
        clearAuthDisabledNotification,
        authenticated,
    } = useAuthProviderV1();
    const { notify } = useNotificationProviderV1();
    const { t } = useTranslation();

    useEffect(() => {
        // Log app version
        console.info(`${t("label.ui-version")}: ${getDataManagerUiVersion()}`);
    }, []);

    useEffect(() => {
        // Slight delay in rendering nav bar helps avoid flicker during initial page redirects
        delay(setShowAppNavBar, 200, true);
    }, []);

    useEffect(() => {
        if (!authenticated) {
            return;
        }

        // Check if Preview Table API is enabled on the backend.
        fetchPreviewTableAPIFT();

        // Check if Dataportal API is enabled on the backend.
        const checkDataportalAPI = async (): Promise<void> => {
            // Check if Dataportal API is enabled on the backend.
            await fetchDataportalAPIFT();

            if (shouldRedirectUserToDP()) {
                // Redirect user to Dataportal by replacing the current URL's subdomain.
                window.location.href = window.location.href.replace(
                    "data",
                    "dp"
                );
            }
        };

        checkDataportalAPI();
    }, [authenticated]);

    useEffect(() => {
        if (authDisabled && authDisabledNotification) {
            // Auth disabled and user not yet notified
            notify(
                NotificationTypeV1.Info,
                t("message.authentication-disabled"),
                false,
                NotificationScopeV1.Global,
                clearAuthDisabledNotification
            );
        }
    }, [authDisabled, authDisabledNotification]);

    return (
        <Box clone paddingTop="64px">
            <AppContainerV1 name={t("label.startree")}>
                <AppHeader />
                {showAppNavBar && <AppNavBar />}
                <AppRouter />
            </AppContainerV1>
        </Box>
    );
};
